import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { DOWNLOAD_EVENT, withDevice } from "@/lib/device";
import { api, QUEUE_EVENT } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";

// Ticket (signed, valid on every server and without cookie) → browser / download manager fetches the file.
async function queueActive() {
  try {
    const { data } = await api.post("/queue/activity");
    if (data.active === false) {
      window.dispatchEvent(new Event(QUEUE_EVENT));
      return false;
    }
  } catch {
    /* admin or network: let the server decide */
  }
  return true;
}

const fileUrl = (url, ticket) => {
  const u = new URL(withDevice(url), window.location.origin);
  if (ticket) u.searchParams.set("ticket", ticket);
  return u.toString();
};

export const useDownload = (url) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [wait, setWait] = useState(null);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const started = useCallback(() => toast.info(t("install.toast"), {
    description: t("install.toastSteps"),
    action: { label: t("install.toastLink"), onClick: () => navigate("/installer-un-apk") },
    duration: 15000,
  }), [t, navigate]);

  const fail = useCallback((retry, direct) => toast.error(t("dl.failed"), {
    description: direct ? <a href={direct} className="underline" data-testid="download-direct-link">{t("dl.directLink")}</a> : undefined,
    action: { label: t("dl.retry"), onClick: retry },
    duration: 20000,
  }), [t]);

  const launch = useCallback((ticket) => {
    window.location.href = fileUrl(url, ticket);
    window.dispatchEvent(new Event(DOWNLOAD_EVENT));
    started();
  }, [url, started]);

  const start = useCallback(async (e) => {
    e?.preventDefault();
    if (wait || !(await queueActive())) return;
    const retry = () => start();
    const poll = async (ticket, tries = 0) => {
      try {
        const { data } = await api.get(`/dl/ticket/${encodeURIComponent(ticket)}`);
        if (data.status === "ready") {
          setWait(null);
          return launch(data.ticket);
        }
        setWait(data);
        timer.current = setTimeout(() => poll(data.ticket), 2000);
      } catch {
        if (tries < 2) {
          timer.current = setTimeout(() => poll(ticket, tries + 1), 1500);
          return;
        }
        setWait(null);
        fail(retry, fileUrl(url, ticket));
      }
    };
    try {
      const { data } = await api.post("/dl/ticket", { url: new URL(url, window.location.origin).pathname });
      if (data.status === "ready") return launch(data.ticket);
      setWait(data);
      timer.current = setTimeout(() => poll(data.ticket), 2000);
    } catch {
      fail(retry, fileUrl(url, null));
    }
  }, [wait, url, launch, fail]);

  return { wait, start, fail };
};

// Landing back from the download URL (?dl=1: link opened without session; ?dl_error=…: server busy).
export const useDownloadReturn = (start, fail, ready) => {
  const [params, setParams] = useSearchParams();
  const done = useRef(false);
  useEffect(() => {
    if (!ready || done.current) return;
    const dl = params.get("dl");
    const err = params.get("dl_error");
    if (!dl && !err) return;
    done.current = true;
    params.delete("dl");
    params.delete("dl_error");
    setParams(params, { replace: true });
    if (err) fail(() => start());
    else start();
  }, [ready, params, setParams, start, fail]);
};
